import { usePositionStorage } from '@/hooks/usePositionStorage'
import { safeRedirectPath, useAuth } from '@/lib'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '../../utilities'
import { AuthErrorAlert } from './auth-error-alert'
import { AuthInput } from './auth-input'
import { AuthSubmitButton } from './auth-submit-button'
import { JobillaBrandLogo } from './jobilla-brand-logo'

export interface RegistrationVerificationFormProps {
	email: string
	password: string
}

export function RegistrationVerificationForm({ email, password }: RegistrationVerificationFormProps) {
	const { t } = useTranslation('common')
	const { verifyRegistration } = useAuth()
	const { getRedirect, clearRedirect } = usePositionStorage()
	const [code, setCode] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	async function handleSubmit(event: React.FormEvent) {
		event.preventDefault()
		setError(null)
		setIsLoading(true)

		try {
			await verifyRegistration(email, code.trim(), password)

			const pendingRedirect = getRedirect()
			clearRedirect()
			window.location.href = safeRedirectPath(pendingRedirect, '/company')
		} catch (err) {
			logger.warn('Registration verification failed', {
				category: 'auth',
				data: { action: 'verify-registration', error: err instanceof Error ? err.message : String(err) },
			})
			setError(t('auth.verifyEmail.errorDefault', 'That code is invalid or has expired.'))
			setIsLoading(false)
		}
	}

	return (
		<div className="w-full max-w-md mx-auto px-8 py-16">
			<JobillaBrandLogo />

			<div className="pt-6">
				<h1 className="text-2xl font-semibold text-gray-800 tracking-[0.01em]">
					{t('auth.verifyEmail.title', 'Verify your email')}
				</h1>
				<p className="mt-2 text-gray-600">
					{t(
						'auth.verifyEmail.description',
						'We sent a verification code to {{email}}. Enter it below to continue.',
						{ email },
					)}
				</p>
			</div>

			<form onSubmit={handleSubmit} className="mt-6 space-y-6">
				<AuthErrorAlert message={error} />

				<AuthInput
					label={t('auth.verifyEmail.codeLabel', 'Verification code')}
					type="text"
					placeholder={t('auth.verifyEmail.codePlaceholder', 'Enter the 6-digit code')}
					value={code}
					onChange={setCode}
					required={true}
					autoComplete="one-time-code"
				/>

				<AuthSubmitButton isLoading={isLoading}>
					{t('auth.verifyEmail.submit', 'Verify')}
				</AuthSubmitButton>
			</form>
		</div>
	)
}
